import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { BACKEND_URI } from "../config/config-vars";
import { Payment } from '../models/payment.model';
import { PaymentSummary } from '../models/payment-summary.model';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  private apiUrl = `${BACKEND_URI}/api/payments`;

  constructor(private http: HttpClient) {}

  async recordPayment(
    salesRepId: string, 
    amount: number, 
    notes: string = '', 
    isFreeMonth: boolean = false
  ): Promise<Payment> {
    try {
      const response = await firstValueFrom(
        this.http.post<Payment>(this.apiUrl, {
          salesRepId,
          amount,
          notes,
          isFreeMonth: isFreeMonth // Explicitly send the isFreeMonth flag
        })
      );

      // Ensure we return the exact server response
      return response;
    } catch (error) {
      console.error('Error recording payment:', error);
      throw error;
    }
  }

  async getPaymentHistory(salesRepId: string): Promise<Payment[]> {
    try {
      return await firstValueFrom(
        this.http.get<Payment[]>(`${this.apiUrl}/${salesRepId}`)
      );
    } catch (error) {
      console.error('Error fetching payment history:', error);
      return [];
    }
  }

  async getPaymentsByPeriod(salesRepId: string, year: number, month: number): Promise<Payment[]> {
    try {
      return await firstValueFrom(
        this.http.get<Payment[]>(`${this.apiUrl}/${salesRepId}/period`, {
          params: {
            year: year.toString(),
            month: month.toString()
          }
        })
      );
    } catch (error) {
      console.error('Error fetching payments by period:', error);
      return [];
    }
  }

  async hasFreeMonthForCurrentPeriod(salesRepId: string): Promise<boolean> {
    try {
      const now = new Date();
      const payments = await this.getPaymentsByPeriod(
        salesRepId,
        now.getFullYear(),
        now.getMonth() + 1
      );
      return payments.some(p => p.isFreeMonth === true);
    } catch (error) {
      console.error('Error checking free month status:', error);
      return false;
    }
  }
// ✅ Global summaries (all users, with optional filters)
async getPaymentSummaries(startDate: string, endDate: string, firstName?: string, lastName?: string): Promise<PaymentSummary[]> {
  try {
    let params = new HttpParams()
      .set('startDate', startDate)
      .set('endDate', endDate);

    if (firstName && lastName) {
      params = params.set('firstName', firstName).set('lastName', lastName);
    }

    return await firstValueFrom(
      this.http.get<PaymentSummary[]>(`${this.apiUrl}/payment-summaries`, { params })
    );
  } catch (error) {
    // Swallow and surface empty list to avoid noisy console errors when backend rejects the range
    return [];
  }
}

// ✅ Per-user summaries (single sales rep)
// payment.service.ts
async getPaymentSummaryForUser(userId: string, startDate: string, endDate: string): Promise<PaymentSummary[]> {
  try {
    return await firstValueFrom(
      this.http.get<PaymentSummary[]>(`${this.apiUrl}/summaries/user/${userId}`, {
        params: { startDate, endDate }
      })
    );
  } catch (e) {
    console.error('Error fetching user payment summary', e);
    return [];
  }
}











}
